import React, { useState, useEffect } from "react";
import {
  FaUtensils,
  FaUniversity,
  FaBook,
  FaBriefcaseMedical,
  FaShoppingCart,
  FaIndustry,
  FaCar,
  FaShieldAlt,
} from "react-icons/fa";

const Industries = () => {
  // State to detect if we're on mobile
  const [isMobile, setIsMobile] = useState(false);

  // Detect window size on mount & when resized
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    // Call it once on mount
    handleResize();
    // Listen for resize
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Industries array
  const industries = [
    {
      icon: <FaUtensils size={30} color="#FF5A5F" />,
      title: "Food & Restaurants",
      description:
        "This broad and innovative industry is creating solutions that simplify our lives.",
      image: "/image/food1.jpg",
      hoverImage: "/image/food.jpg",
    },
    {
      icon: <FaUniversity size={30} color="#FF5A5F" />,
      title: "Banking & Finance",
      description:
        "The user-centric fintech industry strives to make investments easy.",
      image: "/image/industry_bank1.jpg",
      hoverImage: "/image/industry_bank.jpg",
    },
    {
      icon: <FaBook size={30} color="#FF5A5F" />,
      title: "Education",
      description:
        "To fulfill the new demands of the new generation with the latest technologies.",
      image: "/image/industry_education1.jpg",
      hoverImage: "/image/industry_education.jpg",
    },
    {
      icon: <FaBriefcaseMedical size={30} color="#FF5A5F" />,
      title: "Healthcare",
      description:
        "Automating the healthcare industry with breakthrough approaches.",
      image: "/image/industry_health1.jpg",
      hoverImage: "/image/industry_health.jpg",
    },
    {
      icon: <FaShoppingCart size={30} color="#FF5A5F" />,
      title: "Retail",
      description:
        "Enhancing shopping experiences through modern e-commerce and in-store technologies.",
      image: "/image/retail.jpg",
      hoverImage: "/image/retailvertical.jpg",
    },
    {
      icon: <FaIndustry size={30} color="#FF5A5F" />,
      title: "Manufacturing",
      description:
        "Revolutionizing production and supply chains with advanced automation and data insights.",
      image: "/image/manufacturing1.jpg",
      hoverImage: "/image/manufacturing.png",
    },
    {
      icon: <FaShieldAlt size={30} color="#FF5A5F" />,
      title: "Insurance",
      description:
        "Delivering secure, transparent, and personalized insurance services with advanced tech.",
      image: "/image/insurance.jpg",
      hoverImage: "/image/insurancevertical.jpg",
    },
    {
      icon: <FaCar size={30} color="#FF5A5F" />,
      title: "Automobile",
      description:
        "Transforming the automotive world with connected, electric, and autonomous solutions.",
      image: "/image/industry_automobile1.jpg",
      hoverImage: "/image/industry_automobile.jpg",
    },
  ];

  // Build dynamic styles for the grid
  const dynamicStyles = {
    grid: {
      display: "grid",
      // If isMobile => 1 column, else 4 columns
      gridTemplateColumns: isMobile ? "repeat(1, 1fr)" : "repeat(4, 1fr)",
      gap: "30px",
      justifyContent: "center",
      width: "100%",
    },
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.heading}>Industries We Serve</h2>
      <p style={styles.description}>
        We provide superior website and mobile app development services to businesses
        across many different industry verticals.
      </p>

      <div style={dynamicStyles.grid}>
        {industries.map((industry, index) => (
          <IndustryBox key={index} industry={industry} />
        ))}
      </div>
    </div>
  );
};

const IndustryBox = ({ industry }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      style={{
        ...styles.industryBox,
        marginBottom: "20px",
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        style={{
          ...styles.overlayContainer,
          transform: isHovered ? "translateY(0)" : "translateY(100%)",
        }}
      >
        <div style={styles.overlay}></div>
        <img
          src={industry.hoverImage}
          alt={industry.title}
          style={styles.hoverImage}
        />
      </div>

      <div style={styles.icon}>{industry.icon}</div>
      <h3 style={{ ...styles.title, color: isHovered ? "white" : "black" }}>
        {industry.title}
      </h3>
      <p style={{ ...styles.text, color: isHovered ? "white" : "black" }}>
        {industry.description}
      </p>

      <div style={styles.imageContainer}>
        <img src={industry.image} alt={industry.title} style={styles.image} />
      </div>
    </div>
  );
};

// Base styles (unchanged)
const styles = {
  container: {
    textAlign: "center",
    backgroundColor:"#B0E0E6",
    padding: "50px 20px",
  },
  heading: {
    fontSize: "46px",
    fontWeight: "bold",
    marginBottom: "10px",
  },
  description: {
    fontSize: "18px",
    color: "black",
    marginBottom: "40px",
  },
  industryBox: {
    background: "radial-gradient(circle, #1F6788,#161623)",
    border: "2px solid #ccc",
    borderRadius: "10px",
    padding: "20px",
   //boxShadow: "0px 4px 6px rgba(0,0,0,0.1)",
    textAlign: "center",
    transition: "0.3s",
    position: "relative",
    overflow: "hidden",
    minHeight: "280px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    boxShadow: `
    0px 12px 25px rgba(0, 0, 0, 0.6),  
    inset 0px 10px 20px rgba(255, 255, 255, 0.15),
    inset 0px -10px 30px rgba(0, 0, 0, 0.7),
    inset 10px 0px 20px rgba(0, 0, 0, 0.5), 
    inset -10px 0px 20px rgba(255, 255, 255, 0.2)
  `,
  },
  icon: {
    marginBottom: "10px",
    position: "relative",
    zIndex: 2,
  },
  title: {
    fontSize: "38px",
    fontWeight: "bold",
    position: "relative",
    zIndex: 2,
    transition: "color 0.3s ease-in-out",
  },
  text: {
    fontSize: "20px",
    marginBottom: "10px",
    position: "relative",
    zIndex: 2,
    transition: "color 0.3s ease-in-out",
    fontWeight: "bold",
  },
  imageContainer: {
    position: "relative",
    width: "100%",
    height: "200px",
    overflow: "hidden",
    borderRadius: "10px",
    marginTop: "20px",
  },
  image: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    borderRadius: "10px",
  },
  overlayContainer: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    transition: "transform 0.5s ease-in-out",
    zIndex: 1,
  },
  overlay: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "black",
    opacity: 0.6,
  },
  hoverImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
};

export default Industries;